<template>
  <div class="pitch-top">
    <el-tooltip
      :disabled="!editable || !startup.bg_img_url"
      :content="uploadBgImageTooltipText"
      placement="bottom"
    >
      <vb-uploader
        class="image-wrapper pitch-bg"
        :enabled="editable"
        :style="pitchBgStyle"
        :crop="{ enabled: true }"
        @upload-done="onBgUploadDone($event)"
      >
        <img-placeholder
          v-show="!startup.bg_img_url"
          class="background-placeholder img-responsive centered"
        />
        <span v-show="editable && !startup.bg_img_url"
          >Click here or drag &amp; drop an image to upload (1920x1080)</span
        >
      </vb-uploader>
    </el-tooltip>

    <div class="pitch-top-content">
      <div class="container">
        <IntroClassifyControls v-if="showClassifyControls" class="controls" />

        <el-tooltip
          :disabled="!editable"
          :content="uploadLogoTooltipText"
          placement="bottom"
        >
          <div
            class="startup-logo-preview-wrapper absolute -top-12 group"
            :class="{ 'no-image-yet': !startup.logo_url }"
          >
            <vb-uploader
              class="startup-logo-wrapper"
              :enabled="editable"
              :show-icon="false"
              :crop="{
                enabled: true,
                squareCrop: true,
                minWidth: 128,
                minHeight: 128,
                preview: '.startup-logo-preview',
                customModalClass: 'startup-logo-upload-modal',
              }"
              modal-class="startup-logo-upload-modal"
              @upload-done="onLogoUploadDone($event)"
            >
              <a
                v-show="editable"
                href
                class="opacity-0 absolute flex bg-true-gray-900 h-12 w-12 rounded-full items-center justify-center group-hover:opacity-100 transition-opacity duration-200 shadow"
                @click.prevent
              >
                <i class="ion ion-camera text-yellow-600 text-2xl"></i>
              </a>
              <img
                v-show="startup.logo_url"
                :src="startup.logo_url | cld({ w: 128, h: 128, c: 'limit' })"
                :alt="`${startup.name ? startup.name : 'Startup'} logo`"
              />
              <div slot="preview" class="startup-logo-preview-wrapper">
                <div class="startup-logo-wrapper">
                  <div class="startup-logo-preview startup-logo" />
                </div>
              </div>
            </vb-uploader>
          </div>
        </el-tooltip>
        <div class="pitch-controls">
          <a v-show="!editable" href class="btn btn-sm btn-primary hidden"
            ><i class="ion-plus-round before"></i> Follow</a
          >
          <a v-show="!editable" href class="btn btn-sm btn-default hidden"
            ><i class="ion-chatbubble-working before"></i> Message</a
          >
        </div>
        <h1 v-show="!editable" class="inline-block">
          {{ startup.name
          }}<span
            v-if="user.isAdmin && !startup.alive"
            class="text-muted archived-label"
            >(archived)</span
          >
        </h1>
        <input
          v-show="editable"
          class="pitch-title inline-block form-control overview-input"
          placeholder="Add Startup Name"
          :value="startup.name"
          @input="onNameUpdated($event.target.value)"
          @blur="onInputUpdate('name', $event.target.value)"
        />
        <p
          v-if="!isStartupNameDefined"
          class="name-field-error text-danger text-sm"
        >
          Please provide company name
        </p>
        <p v-if="!editable" class="pitch-company-type">
          <SimpleItemList :items="startup.sectors">
            <template slot-scope="{ item }">
              <span>{{ item | sectorLabel }}</span>
            </template>
          </SimpleItemList>
        </p>
        <p v-if="editable" class="pitch-company-type">
          <SectorsTagsEditor
            class="seamless overview"
            :value="startup.sectors"
            @input="onSectorsChange"
          />
        </p>

        <IntroActionControlls
          v-if="user.id"
          class="margin-top-15"
          :startup="startup"
        />

        <el-alert
          v-if="user.isAdmin && !startup.alive"
          class="margin-top-10 margin-bottom-10"
          title="This startup has been archived and is only visible for admins"
          type="error"
          effect="dark"
          :closable="false"
        />

        <div v-if="editable" class="intro-textarea el-textarea">
          <textarea
            v-autosize
            placeholder="Add 1-2 Sentences Description of the Project"
            :value="startup.caption"
            maxlength="250"
            rows="2"
            @blur="onInputUpdate('caption', $event.target.value)"
            @input="onStartupCaptionUpdate('caption', $event.target.value)"
          />
          <span class="el-input__count">
            {{ startup.caption.length }}/250
          </span>
        </div>
        <p
          v-if="!editable"
          class="pitch-company-description"
          :class="{ 'no-sectors': !this.startup.sectors.length }"
        >
          {{ startup.caption }}
        </p>
        <ul
          class="pitch-details"
          :class="{ 'pitch-details-editable': editable }"
        >
          <li v-if="editable || startup.website">
            <a v-show="!editable" :href="websiteLink" target="_new">
              {{ startup.website }}
            </a>
            <input
              v-show="editable"
              class="form-control overview-input"
              placeholder="Paste Your Website Link"
              :value="startup.website"
              @blur="onInputUpdate('website', $event.target.value)"
            />
          </li>
          <li v-if="!editable">
            {{ startup.country | countryName }}
            <span v-show="startup.country && startup.city">,</span>
            {{ startup.city | capitalize }}
          </li>
          <li v-if="editable">
            <CountryDropdown class="seamless overview" />
          </li>
          <li v-if="editable">
            <input
              v-show="editable"
              class="form-control overview-input"
              placeholder="Type City"
              :value="startup.city"
              @blur="onInputUpdate('city', $event.target.value)"
            />
          </li>
        </ul>
        <div v-show="user.isAdmin" :class="{ 'pitch-top-buttons': editable }">
          <router-link
            v-show="editable"
            :to="{ name: 'adminStartupSettings', params: { id: startup.id } }"
            class="btn btn-sm btn-info btn-transparent"
          >
            Settings
          </router-link>
          <router-link
            v-show="editable"
            :to="{
              name: 'adminStartupFunnel',
              params: { startupId: startup.id },
            }"
            class="btn btn-sm btn-info btn-transparent"
          >
            Funnel
          </router-link>
          <router-link
            v-show="editable"
            :to="{ name: 'adminStartupLinks', params: { id: startup.id } }"
            class="btn btn-sm btn-info btn-transparent"
          >
            Links
          </router-link>
          <el-tooltip content="Click to unpublish" placement="top">
            <a
              v-show="editable && startup.is_published"
              href
              class="btn btn-sm btn-success btn-transparent"
              @click.prevent="conceal()"
              >Is published</a
            >
          </el-tooltip>
          <el-tooltip content="Click to publish" placement="top">
            <a
              v-show="editable && !startup.is_published"
              href
              class="btn btn-sm btn-warning btn-transparent"
              @click.prevent="publish()"
              >Not published</a
            >
          </el-tooltip>
          <a
            v-show="editable && startup.alive"
            href
            class="btn btn-sm btn-danger btn-transparent"
            @click.prevent="$refs.confirmRemoveModal.open()"
            >Remove</a
          >
          <a
            v-show="editable && !startup.alive"
            href
            class="btn btn-sm btn-danger btn-transparent"
            disabled
            @click.prevent
            >Already removed</a
          >
          <ClassifyDropdown
            v-show="(user.isInvestor || user.isAdmin) && editable"
          />
        </div>
      </div>
    </div>
    <confirm-modal
      ref="confirmRemoveModal"
      :show-loading="isRemoveInProgress"
      @confirm="remove()"
    >
      <h4 slot="modal-header" class="modal-title text-danger">
        Remove startup
      </h4>
      <div slot="modal-body">
        <p>Are you sure you want to remove startup?</p>
      </div>
    </confirm-modal>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import { sectorLabel } from '@/services/sectors'
import { countryName } from '@/services/countries'
import ComputedStartup from '@/services/startup'
import http from '@/services/http'
import { sleep } from '@/utils'
import { cloudinaryTransform } from '@/filters'
import CountryDropdown from './CountryDropdown.vue'
import ClassifyDropdown from './ClassifyDropdown.vue'
import IntroClassifyControls from './IntroClassifyControls.vue'
import IntroActionControlls from './IntroActionControlls.vue'
import SectorsTagsEditor from '@/components/SectorsTagsEditor.vue'
import autosize from '@/directives/autosize'
import _ from 'lodash'

export default {
  components: {
    CountryDropdown,
    ClassifyDropdown,
    IntroActionControlls,
    IntroClassifyControls,
    SectorsTagsEditor,
  },
  directives: { autosize },
  filters: { countryName, sectorLabel },
  props: {
    startup: {
      type: Object,
      default: () => ({}),
    },
    editable: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isNameInputValueDefined: false,
      isRemoveInProgress: false,
    }
  },
  computed: {
    ...ComputedStartup,
    ...mapState({
      user: (state) => state.user.data,
      showClassifyControls: (state) =>
        state.startupProfileInvestorClassifies.isClassificationEnabled ||
        state.user.data.isOrganizer,
    }),
    ...mapGetters({
      websiteLink: 'startupProfile/websiteLink',
    }),
    isStartupNameDefined() {
      return !!this.startup.name || this.isNameInputValueDefined
    },
    tooltipClass() {
      return this.editable ? '' : 'tooltip-hidden'
    },
    uploadLogoTooltipText() {
      const tooltipText = this.editable ? 'Click to upload a new logo' : ''

      return tooltipText
    },
    uploadBgImageTooltipText() {
      const tooltipText = this.editable
        ? 'Click to change a background image (1920x1080)'
        : ''

      return tooltipText
    },
    pitchBgStyle() {
      if (!this.startup.bg_img_url) {
        return {}
      }
      const url = cloudinaryTransform(this.startup.bg_img_url, {
        w: 1920,
        h: 1080,
        c: 'limit',
      })
      return { 'background-image': `url(${url})` }
    },
  },
  methods: {
    onAttrChange(obj) {
      this.$emit('on-startup-attr-change', obj)
      this.$store.dispatch('startupProfile/setAttr', obj)
    },
    onInputUpdate(attr, value) {
      if (this.startup[attr] !== value) {
        this.$store.dispatch('startupProfile/setAttr', { attr, value })
      }
    },
    onInputDebounced: _.debounce(function (attr, value) {
      this.onInputUpdate(attr, value)
    }, 250),
    onStartupCaptionUpdate(attr, value) {
      this.onInputDebounced(attr, value)
    },
    onLogoUploadDone(event) {
      const self = this
      const reader = new FileReader()
      // trick to immediately show image being uploaded
      // otherwise there is a lag as browser must download logo from cloudinary first
      reader.onload = function (e) {
        self.onAttrChange({ attr: 'logo_url', value: e.target.result })
        self.$nextTick(() => {
          self.onAttrChange({ attr: 'logo_url', value: event.data.url })
          self.$forceUpdate()
        })
      }
      reader.readAsDataURL(event.file)
    },
    onBgUploadDone(event) {
      this.$store.commit(
        'startupProfile/UPDATE_BACKGROUND_IMAGE',
        event.data.url,
      )
      this.$forceUpdate()
    },
    publish() {
      this.$store.dispatch('startupProfile/publish', { id: this.startup.id })
    },
    conceal() {
      this.$store.dispatch('startupProfile/conceal', { id: this.startup.id })
    },
    async remove() {
      this.isRemoveInProgress = true
      const response = http.postAuthed(`/startups/${this.startup.id}/delete`)
      await sleep(2000)
      await response
      this.isRemoveInProgress = false
      this.$store.dispatch('notifications/addItem', {
        type: 'success',
        content: 'Startup removed successfully',
      })
      this.$refs.confirmRemoveModal.closeAndRedirect({
        name: 'adminStartupList',
      })
    },

    onNameUpdated(name) {
      this.onNameUpdatedDebounced(name)
    },

    onNameUpdatedDebounced: _.debounce(function (name) {
      this.isNameInputValueDefined = !!name
      if (name) {
        this.$store.dispatch('startupProfile/setAttr', {
          attr: 'name',
          value: name,
        })
      }
    }, 200),

    onSectorsChange(sectors) {
      console.log(' => Intro.vue onSectorsChange:', sectors)
      this.onAttrChange({ attr: 'sectors', value: sectors })
    },
  },
}
</script>

<style lang="scss">
@import '~assets/css/colors.scss';
@import '~assets/css/vars.scss';

.pitch-bg {
  span {
    color: $grey60;
    position: absolute;
    top: 58%;
    left: 50%;
    margin: 0;
    transform: translate(-50%, -50%);
    border-radius: 3px;
    padding: 10px;
  }
}

.background-placeholder {
  height: 90%;
  border-radius: 0px;
  background: $grey150;
  margin: 14px 16px 0px 16px;
  box-shadow: none;
}

.pitch-logo-upload {
  height: 120px;
  width: 120px;
  border-radius: 3px;

  @apply flex justify-center items-center overflow-hidden;

  .logo-placeholder {
    height: 90%;
    border: 0px;
    background: transparent;
  }

  .hover-icon-logo {
    top: 40px;
    left: 50%;
    padding: 8px 9px;
  }
}

.pitch-top-content {
  .name-field-error {
    margin-left: 150px;
  }

  input {
    background: transparent;
  }

  .controls {
    position: absolute;
    top: 20px;
    right: 15px;
  }

  .pitch-title {
    margin: 16px 0 16px 150px;
    width: 492px;
    height: 50px;
    font-size: 26px;
    font-weight: bold;
    color: $grey30;
  }

  .pitch-company-type div {
    min-width: 244px;
    margin-bottom: 16px;
  }

  .intro-textarea {
    width: 642px;

    textarea {
      color: $grey60;
      border-radius: 0;
      border: none;
      border-bottom: 1px solid $grey160;
      transition: border-color 0.3s ease;
      outline: none;
      background: transparent;
      line-height: 1.7;

      @apply w-full text-base;

      &:hover,
      &:focus {
        color: $grey30;
      }

      &:focus {
        border: none;
        border-bottom: 1px solid $brand-primary;
        box-shadow: none;
      }
    }

    .el-input__count {
      bottom: 7px;
    }
  }

  .pitch-details-editable li {
    width: 198px;

    input {
      height: 42px;
    }
  }

  .pitch-top-buttons {
    margin-bottom: 32px;
  }

  .archived-label {
    font-size: 16px;
    margin-left: 12px;
  }
}

.startup-logo-upload-modal {
  .pitch-logo-wrapper {
    width: 120px;
    height: 120px;

    @apply flex;
  }

  .pitch-logo {
    //padding: 0;
  }
}
</style>
